<template>
  <section>
    <v-divider class="my-6" />
    <h5 class="wc-h5 mb-2">Submiembros del miembro titular</h5>
    <span>
      Agrega o edita submiembros/colaboradores asignados a este miembro.
      Los cambios se aplican de inmediato.
    </span>

    <v-row class="mt-2 mb-2" align="center">
      <v-col cols="12" md="9">
        <v-text-field
          v-model="search"
          filled
          dense
          rounded
          prepend-inner-icon="mdi-magnify"
          label="Buscar por nombre, apellido o correo"
          hide-details
          @keyup.enter="onSearch"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          filled
          dense
          rounded
          hide-details
          label="Estado"
          @change="onSearch"
        />
      </v-col>
    </v-row>
    <v-row class="mb-2" align="center" justify="end">
      <v-col cols="12" md="auto" class="d-flex flex-wrap justify-end">
        <v-btn
          elevation="0"
          class="white--text text-bold rounded-pill"
          :color="color.primaryVariant"
          :disabled="limitReached"
          @click="openCreate"
        >
          <v-icon class="mr-2">mdi-account-plus-outline</v-icon>
          Agregar submiembro
        </v-btn>
      </v-col>
    </v-row>

    <v-alert
      v-if="limitReached"
      type="warning"
      dense
      text
      class="mb-2"
    >
      Has alcanzado el límite de {{ limitValue }} submiembro(s) permitidos para este miembro.
    </v-alert>

    <v-alert
      v-if="errorMessage"
      type="error"
      dense
      text
      class="mb-2"
    >
      {{ errorMessage }}
    </v-alert>

    <div v-if="loading" class="d-flex align-center my-4">
      <v-progress-circular indeterminate size="20" width="2" class="mr-2" />
      <span>Cargando submiembros…</span>
    </div>

    <v-data-table
      v-else
      :headers="headers"
      :items="submembers"
      :no-data-text="
        search
          ? 'No se encontraron submiembros con el filtro aplicado.'
          : 'Este miembro aún no tiene submiembros asignados.'
      "
      class="elevation-0"
      hide-default-footer
    >
      <template v-slot:item.name="{ item }">
        <div class="d-flex flex-column">
          <span class="font-weight-medium">
            {{ fullName(item) }}
          </span>
          <span class="text-caption grey--text text--darken-1">
            {{ item.email }}
          </span>
        </div>
      </template>
      <template v-slot:item.position="{ item }">
        <span v-if="submemberPosition(item)">{{ submemberPosition(item) }}</span>
        <span v-else class="grey--text text--darken-1">—</span>
      </template>
      <template v-slot:item.validationStatus="{ item }">
        <v-chip
          small
          :color="statusColor(item.validationStatus).bg"
          :text-color="statusColor(item.validationStatus).text"
        >
          {{ statusColor(item.validationStatus).label }}
        </v-chip>
      </template>
      <template v-slot:item.createdAt="{ item }">
        <span v-if="item.createdAt">{{ formatDate(item.createdAt) }}</span>
        <span v-else class="grey--text text--darken-1">—</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          :color="color.primaryVariant"
          :title="'Editar submiembro'"
          @click="openEdit(item)"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <div
      v-if="totalDocs > 0"
      class="d-flex align-center justify-space-between mt-2 px-2"
    >
      <span class="text-caption grey--text text--darken-1">
        Mostrando {{ pageRangeStart }}–{{ pageRangeEnd }} de {{ totalDocs }}
        {{ totalDocs === 1 ? 'submiembro' : 'submiembros' }}
      </span>
      <div class="d-flex align-center">
        <v-btn
          icon
          small
          :disabled="!canGoPrev || loading"
          :title="'Página anterior'"
          @click="goToPrevPage"
        >
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        <span class="mx-3 text-caption">
          Página <strong>{{ currentPage }}</strong> de {{ totalPages }}
        </span>
        <v-btn
          icon
          small
          :disabled="!canGoNext || loading"
          :title="'Página siguiente'"
          @click="goToNextPage"
        >
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="formDialog" max-width="760" persistent>
      <v-card color="white" class="pa-4 admin-submembers-form-dialog">
        <div class="d-flex align-center mb-2">
          <h5 class="wc-h5 mb-0">
            {{ editingId ? 'Editar submiembro' : 'Agregar submiembro' }}
          </h5>
          <v-spacer />
          <v-btn icon small @click="closeForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <span class="grey--text text--darken-1">
          {{ editingId ? 'Actualiza la información del submiembro.' : 'Crea un submiembro y asígnalo a este miembro titular.' }}
        </span>
        <v-form
          ref="submemberForm"
          v-model="formValid"
          class="mt-4"
          @submit.prevent="save"
        >
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.name"
                filled
                dense
                rounded
                label="Nombre(s) *"
                :rules="[requiredRule]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.firstLastname"
                filled
                dense
                rounded
                label="Apellido paterno *"
                :rules="[requiredRule]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.secondLastname"
                filled
                dense
                rounded
                label="Apellido materno"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.email"
                filled
                dense
                rounded
                label="Correo electrónico *"
                type="email"
                :rules="[requiredRule, emailRule]"
                :disabled="!!editingId"
              />
            </v-col>
          </v-row>

          <template v-if="customFieldGroups.length || loadingFormFields">
            <v-divider class="my-4" />
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="font-weight-bold primary--text">
                Campos del formulario de submiembros
              </span>
              <span class="text-caption grey--text text--darken-1">
                Los campos marcados con * son obligatorios.
              </span>
            </div>
            <div
              v-if="loadingFormFields"
              class="d-flex align-center justify-center my-4"
            >
              <v-progress-circular indeterminate size="24" width="2" class="mr-2" />
              <span>Cargando campos del formulario…</span>
            </div>
            <template v-else>
              <div
                v-for="(group, groupIndex) in customFieldGroups"
                :key="group._id || `group-${groupIndex}`"
              >
                <div
                  v-if="group.title"
                  class="font-weight-bold primary--text mt-3 mb-1"
                >
                  {{ group.title }}
                </div>
                <v-row>
                  <v-col
                    v-for="field in group.fields"
                    :key="field._id || field.formattedName"
                    cols="12"
                    md="6"
                  >
                    <SubmemberFormField
                      variant="vuetify"
                      :field="field"
                      :value="customFieldsData[field.formattedName]"
                      @update="onFieldUpdate(field.formattedName, $event)"
                    />
                  </v-col>
                </v-row>
              </div>
            </template>
          </template>
        </v-form>
        <v-alert v-if="formError" type="error" dense text class="mb-2">
          {{ formError }}
        </v-alert>
        <div class="d-flex justify-end mt-2">
          <v-btn
            text
            class="mr-2"
            :disabled="saving"
            @click="closeForm"
          >
            Cancelar
          </v-btn>
          <v-btn
            elevation="0"
            class="white--text text-bold rounded-pill"
            :color="color.primaryVariant"
            :loading="saving"
            :disabled="saving || loadingFormFields"
            @click="save"
          >
            <v-icon class="mr-2">mdi-content-save</v-icon>
            {{ editingId ? 'Guardar cambios' : 'Crear submiembro' }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { colors } from '@/constants/colors.js'
import SubmemberFormField from './SubmemberFormField.vue'
import {
  buildMemberDataFromDefs,
  buildSubmemberCustomFields,
  groupFieldsBySection,
  isEmptySubmemberFieldValue,
} from '@/utils'

const STATUS_META = {
  ACTIVE: { label: 'Activo', bg: '#c8e6c9', text: '#1b5e20' },
  PROSPECTO: { label: 'Prospecto', bg: '#fff3cd', text: '#856404' },
  INACTIVO: { label: 'Inactivo', bg: '#e0e0e0', text: '#424242' },
  EXPIRED: { label: 'Expirado', bg: '#ffe0b2', text: '#e65100' },
  DELETED: { label: 'Eliminado', bg: '#ffcdd2', text: '#b71c1c' },
}

export default {
  name: 'AdminSubmembersSection',
  components: {
    SubmemberFormField,
  },
  props: {
    memberId: {
      type: String,
      default: '',
    },
    limitValue: {
      type: Number,
      default: 0,
    },
    organizationId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      color: colors,
      search: '',
      statusFilter: '',
      currentPage: 1,
      formDialog: false,
      formValid: false,
      formError: null,
      editingId: null,
      form: {
        name: '',
        firstLastname: '',
        secondLastname: '',
        email: '',
      },
      memberFields: [],
      formSections: [],
      customFieldsData: {},
      formConfigLoaded: false,
      loadedOrganizationId: '',
      loadingFormFields: false,
      saving: false,
      errorMessage: null,
    }
  },
  computed: {
    ...mapState('adminSubmembers', {
      submembers: (state) => state.submembers || [],
      totalSubmembers: (state) => state.totalSubmembers || 0,
      loading: (state) => state.loading,
      totalDocs: (state) => state.totalDocs,
      totalPages: (state) => state.totalPages,
      page: (state) => state.page,
    }),
    headers() {
      return [
        { text: 'Submiembro', value: 'name', sortable: false },
        { text: 'Cargo', value: 'position', sortable: false },
        { text: 'Estado', value: 'validationStatus', sortable: false },
        { text: 'Alta', value: 'createdAt', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'end' },
      ]
    },
    statusOptions() {
      return [
        { text: 'Todos los estados', value: '' },
        { text: 'Activo', value: 'ACTIVE' },
        { text: 'Pendiente', value: 'PROSPECTO' },
        { text: 'Inactivo', value: 'INACTIVO' },
        { text: 'Expirado', value: 'EXPIRED' },
      ]
    },
    limitReached() {
      return this.limitValue > 0 && this.totalSubmembers >= this.limitValue
    },
    canGoPrev() {
      return this.currentPage > 1
    },
    canGoNext() {
      return this.currentPage < this.totalPages
    },
    pageRangeStart() {
      if (!this.totalDocs) return 0
      return (this.currentPage - 1) * 10 + 1
    },
    pageRangeEnd() {
      if (!this.totalDocs) return 0
      return Math.min(this.currentPage * 10, this.totalDocs)
    },
    customFieldGroups() {
      return groupFieldsBySection(this.formSections, this.memberFields).filter(
        (group) => group.fields && group.fields.length > 0
      )
    },
    dynamicPositionField() {
      return this.memberFields.find(this.isDynamicPositionField) || null
    },
  },
  watch: {
    memberId: {
      immediate: true,
      handler(next, previous) {
        if (!next) return
        if (next !== previous) {
          this.currentPage = 1
          this.refresh()
        } else if (!previous) {
          this.refresh()
        }
      },
    },
  },
  methods: {
    ...mapActions('adminSubmembers', [
      'fetchAdminSubmembers',
      'createAdminSubmember',
      'updateAdminSubmember',
    ]),
    ...mapActions('registrationFields', ['getSubMemberFormFields']),
    ...mapActions('formSections', ['getFormSectionsByOrg']),
    ...mapMutations('notification', ['show']),
    requiredRule(v) {
      return (v !== null && v !== undefined && String(v).trim() !== '') || 'Este campo es obligatorio'
    },
    emailRule(v) {
      if (!v) return true
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Ingresa un correo válido'
    },
    fullName(item) {
      if (!item) return ''
      return [item.name, item.firstLastname, item.secondLastname]
        .filter(Boolean)
        .join(' ')
        .trim()
    },
    isDynamicPositionField(field) {
      if (!field) return false
      if (field.formattedName === 'cargo_posicion') return true
      const name = String(field.name || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z]/g, '')
        .toLowerCase()
      return name === 'cargoposicion'
    },
    submemberPosition(item) {
      if (!item) return ''
      if (item.position) return item.position
      const positionEntry = (item.memberData || []).find(
        (entry) => this.isDynamicPositionField(entry)
      )
      if (!positionEntry || positionEntry.value === null || positionEntry.value === undefined) {
        return ''
      }
      return Array.isArray(positionEntry.value)
        ? positionEntry.value.join(', ')
        : String(positionEntry.value)
    },
    statusColor(status) {
      return (
        STATUS_META[status] || { label: status || 'N/D', bg: '#e0e0e0', text: '#424242' }
      )
    },
    formatDate(value) {
      try {
        return new Date(value).toLocaleDateString('es-MX', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      } catch (e) {
        return value
      }
    },
    refresh() {
      if (!this.memberId) return
      this.errorMessage = null
      this.currentPage = 1
      this.fetchPage(1)
    },
    fetchPage(page) {
      if (!this.memberId) return
      this.errorMessage = null
      this.fetchAdminSubmembers({
        memberId: this.memberId,
        query: {
          page,
          name: this.search.trim() || undefined,
          validationStatus: this.statusFilter || undefined,
        },
      })
        .then((res) => {
          if (!res || res.status === false) {
            this.errorMessage = res?.message || 'No fue posible cargar los submiembros'
            return
          }
          const data = res.data || {}
          const totalPages = Number(data.totalPages) || 0
          const returnedPage = Number(data.page) || page
          if (totalPages && returnedPage > totalPages) {
            this.currentPage = totalPages
            this.fetchPage(totalPages)
          } else {
            this.currentPage = returnedPage
          }
        })
    },
    onSearch() {
      this.currentPage = 1
      this.fetchPage(1)
    },
    goToPrevPage() {
      if (!this.canGoPrev || this.loading) return
      this.fetchPage(this.currentPage - 1)
    },
    goToNextPage() {
      if (!this.canGoNext || this.loading) return
      this.fetchPage(this.currentPage + 1)
    },
    resetForm() {
      this.form = {
        name: '',
        firstLastname: '',
        secondLastname: '',
        email: '',
      }
      this.customFieldsData = {}
      this.formValid = false
      this.formError = null
    },
    async loadFormConfig() {
      const organizationId = this.organizationId
      if (!organizationId) {
        this.formConfigLoaded = false
        this.formError = 'No fue posible determinar la organización del formulario.'
        return false
      }
      if (this.loadedOrganizationId === organizationId) return true

      this.loadingFormFields = true
      this.formConfigLoaded = false
      this.formError = null
      try {
        const [fieldsResponse, sectionsResponse] = await Promise.all([
          this.getSubMemberFormFields(organizationId),
          this.getFormSectionsByOrg(organizationId),
        ])

        if (!fieldsResponse?.status || !Array.isArray(fieldsResponse.data?.registrationFields) || !sectionsResponse?.status) {
          this.formConfigLoaded = false
          this.formError = 'No fue posible cargar los campos del formulario.'
          this.show({
            text: this.formError,
            color: 'warning',
          })
          return false
        }

        const registrationFields =
          (fieldsResponse.data && fieldsResponse.data.registrationFields) || []
        this.memberFields = registrationFields.filter(
          (field) => field.fieldType !== 'DOC'
        )
        this.formSections =
          (sectionsResponse && sectionsResponse.status && sectionsResponse.data
            ? sectionsResponse.data.sections || []
            : []) || []
        this.loadedOrganizationId = organizationId
        this.formConfigLoaded = true
        return true
      } catch (error) {
        this.formConfigLoaded = false
        this.formError = 'No fue posible cargar los campos del formulario.'
        this.show({
          text: this.formError,
          color: 'warning',
        })
        return false
      } finally {
        this.loadingFormFields = false
      }
    },
    hydrateCustomFields(item) {
      const memberData = buildMemberDataFromDefs(
        Array.isArray(item.memberData) ? item.memberData : [],
        this.memberFields
      )
      const values = {}
      this.memberFields.forEach((field) => {
        const entry = memberData.find(
          (candidate) => candidate.formattedName === field.formattedName
        )
        values[field.formattedName] = entry ? entry.value : null
      })

      if (
        this.dynamicPositionField &&
        isEmptySubmemberFieldValue(
          values[this.dynamicPositionField.formattedName]
        ) &&
        item.position
      ) {
        values[this.dynamicPositionField.formattedName] = item.position
      }

      this.customFieldsData = values
    },
    onFieldUpdate(formattedName, value) {
      this.$set(this.customFieldsData, formattedName, value)
    },
    effectivePosition() {
      // El cargo se captura únicamente en el campo dinámico del formulario de
      // submiembros (Cargo/Posición), no en un campo fijo adicional.
      if (!this.dynamicPositionField) return ''
      const value = this.customFieldsData[this.dynamicPositionField.formattedName]
      if (value === null || value === undefined) return ''
      return Array.isArray(value) ? value.join(', ') : String(value)
    },
    async validateForm() {
      await this.$nextTick()
      if (this.$refs.submemberForm && this.$refs.submemberForm.validate) {
        this.formValid = this.$refs.submemberForm.validate()
      }
    },
    async openCreate() {
      this.editingId = null
      this.resetForm()
      this.formDialog = true
      await this.loadFormConfig()
      await this.validateForm()
    },
    async openEdit(item) {
      this.editingId = item._id
      this.resetForm()
      this.form = {
        name: item.name || '',
        firstLastname: item.firstLastname || '',
        secondLastname: item.secondLastname || '',
        email: item.email || '',
      }
      this.formDialog = true
      const loaded = await this.loadFormConfig()
      if (loaded) this.hydrateCustomFields(item)
      await this.validateForm()
    },
    closeForm() {
      this.formDialog = false
      this.editingId = null
      this.formError = null
      this.formValid = false
    },
    async save() {
      if (this.saving) return
      if (!this.formConfigLoaded) {
        this.formError = 'No fue posible cargar los campos del formulario.'
        return
      }
      this.formError = null
      await this.$nextTick()

      let valid = this.formValid
      if (this.$refs.submemberForm && this.$refs.submemberForm.validate) {
        valid = this.$refs.submemberForm.validate()
      }
      if (!valid) {
        this.formError = 'Revisa los campos obligatorios del formulario.'
        return
      }

      if (this.loadedOrganizationId !== this.organizationId) {
        this.formConfigLoaded = false
        this.formError = 'La organización cambió. Vuelve a abrir el formulario.'
        return
      }
      this.saving = true
      const isEditing = Boolean(this.editingId)
      const payload = {
        name: String(this.form.name || '').trim(),
        firstLastname: String(this.form.firstLastname || '').trim(),
        secondLastname: String(this.form.secondLastname || '').trim(),
        email: String(this.form.email || '').trim().toLowerCase(),
      }
      // El cargo proviene del campo dinámico Cargo/Posición. Si la organización
      // no tiene ese campo, no se envía para no borrar el valor almacenado.
      if (this.dynamicPositionField) {
        payload.position = String(this.effectivePosition() || '').trim()
      }
      const customFields = buildSubmemberCustomFields(
        this.memberFields,
        this.customFieldsData,
        { includeEmpty: true }
      )

      try {
        const action = isEditing
          ? this.updateAdminSubmember({
              memberId: this.memberId,
              submemberId: this.editingId,
              user: payload,
              customFields,
            })
          : this.createAdminSubmember({
              memberId: this.memberId,
              user: payload,
              customFields,
            })
        const res = await action

        if (res?.status === false) {
          this.formError = res.message || 'No fue posible guardar el submiembro'
          this.show({
            text: this.formError,
            color: 'error',
          })
          return
        }
        this.show({
          text: isEditing
            ? 'Submiembro actualizado correctamente'
            : 'Submiembro creado correctamente',
          color: 'success',
        })
        this.closeForm()
        this.refresh()
        this.$emit('updated')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.admin-submembers-form-dialog {
  max-height: 90vh;
  overflow-y: auto;
}
</style>
